import cn from "classnames"

import { Button, Col, Divider, Flex, Row, Text } from "@/atoms"
import { SaveOutlined, VmIcons } from "@/icons"
import { CancelConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import { Heading } from "@/molecules/Heading"
import { ModulesTree } from "@/molecules/ModulesTree/ModulesTree"
import { ROOT_KEY } from "@/molecules/ModulesTree/ModulesTree.types"
import { ConfigurationBadge } from "@/organisms/Drawers/components/ConfigurationBadge/ConfigurationBadge"
import {
  DeleteConfirmModalConfiguration,
  FooterConfigurationTemplate
} from "@/organisms/Drawers/components/FooterConfigurationTemplate"
import { PlaceholderConfiguration } from "@/organisms/Drawers/components/PlaceholderConfiguration"
import { Loader } from "@/pages/SettingsPage/components/molecules/Loader"
import { VerticalTabTemplate } from "@/pages/SettingsPage/components/templates/VerticalTabTemplate"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ModalConfigurationTabVirtualMachineDrawer } from "../Drawers/components/VirtualMachineDrawer/components/ConfigurationTabVirtualMachineDrawer/components/ModalConfigurationTabVirtualMachineDrawer"
import { MonorepoConfigurationsForm } from "./ModuleConfigurationsForm/MonorepoConfigurationsForm"

import styles from "./MonorepoConfigurations.module.scss"

import { ReadInstructionButton } from "@/molecules/ReadInstructionButton"
import type {
  Configs,
  MonorepoConfigurationsProps
} from "./MonorepoConfigurations.types"

type Node = {
  key: string
  children?: Node[]
}

function getAllKeys(node: Node): string[] {
  // This will store all the keys
  const keys: string[] = []

  // A recursive function to traverse the tree
  function traverse(currentNode: Node): void {
    // Add the current node's key to the keys array
    keys.push(currentNode.key)
    if (!currentNode.children) return
    // Recurse over each of the children nodes
    for (const child of currentNode.children) {
      traverse(child)
    }
  }

  // Start the traversal from the input node
  traverse(node)

  // Return the collected keys
  return keys
}

function getAllAvailablePaths(paths: string[]): string[] {
  const result: Set<string> = new Set()

  paths.forEach((path) => {
    const components = path.split("/")
    let currentPath = ""

    components.forEach((component, index) => {
      currentPath += component
      result.add(currentPath)

      // Add separator for next component if it's not the last one
      if (index < components.length - 1) {
        currentPath += "/"
      }
    })
  })

  return Array.from(result)
}

export function MonorepoConfigurations(props: MonorepoConfigurationsProps) {
  const {
    configLoading = false,
    modules = [],
    repoName,
    scm = "OnPrem",
    configs,
    repoPathsVM,
    onSave,
    onReset,
    loading = false
  } = props
  const normalizedModules = useMemo(() => {
    return modules.map((module) => ({
      ...module,
      path: module.path.replace(/^\//, "")
    }))
  }, [modules])
  const [selected, setSelected] = useState<string>("")
  const [highlights, setHighlights] = useState<string[]>([selected])
  const [currentConfigs, setCurrentConfigs] = useState<Configs>(configs || {})
  const [open, setOpen] = useState<any[] | false>(false)
  const { confirm } = useConfirmModal()
  const { t } = useTranslation()
  const keyPrefix = "inventory.codeRepositoriesTab.drawer"
  const { t: tConfig } = useTranslation("translation", {
    keyPrefix: `${keyPrefix}.config`
  })
  const edited = useMemo(() => {
    return Object.entries(configs || {})
      .filter(
        ([_, conf]) => conf.splitPath || conf.imageRepositories.length > 0
      )
      .map(([path, _]) => path)
  }, [configs])

  useEffect(() => {
    if (!loading) setCurrentConfigs(configs || {})
  }, [configs, loading])

  const formatProjectName = (selected: string) => {
    if (selected === ROOT_KEY) return repoName
    if (selected.startsWith("/")) return `${repoName}${selected}`
    return `${repoName}/${selected}`
  }

  const projectName = formatProjectName(selected)
  const selectedConfigs = currentConfigs[
    selected === ROOT_KEY ? "" : selected!
  ] || {
    projectName,
    imageRepositories: [],
    exclude: false,
    splitPath: false
  }

  const handleSave = () => {
    const onlyNoneEmpty = Object.fromEntries(
      Object.entries(currentConfigs).filter(
        ([_, value]) =>
          value.splitPath || value.exclude || value.imageRepositories.length > 0
      )
    )
    onSave(onlyNoneEmpty)
  }

  const isFile = (path: string) => {
    return !!normalizedModules.find((module) => module.path === path)
  }

  const dirname = (path: string) => {
    return path.split("/").slice(0, -1).join("/").replace(/^\/+/, "")
  }

  const allAdjacentModules = (path: string) => {
    const dir = dirname(path)
    const inSameDir = normalizedModules
      .filter((module) => module.path.startsWith(dir))
      .map((module) => module.path)
    return getAllAvailablePaths(inSameDir).filter((p) => p.startsWith(dir))
  }

  /**
   * Reset the configurations of the selected module by removing it from the currentConfigs
   * and calling save
   */
  const handleResetSelected = () => {
    if (!selected) return

    if (selected === ROOT_KEY) {
      return onReset()
    }

    const { [selected]: _, ...rest } = currentConfigs

    return onSave(rest, { noMessage: true })
  }

  const disabledFooterButtons = currentConfigs === configs
  const onResetSelected = () => {
    setHighlights([])
    setSelected("")
  }
  const onCancel = () => {
    setCurrentConfigs(configs || {})
    onResetSelected()
  }

  return (
    <VerticalTabTemplate
      placement="drawer"
      title={
        <Flex justify="space-between" align="center" className={styles.title}>
          <Flex flex={1} vertical gap={4}>
            <Heading level={5} title="Repository Tree" />
            <Text type="secondary" className="sm-text-ks">
              Split code repository to an independent Triage projects after
              splitting you can
            </Text>
            <Text type="secondary" className="sm-text-ks">
              correlate Images / VMs manually.
            </Text>
          </Flex>
        </Flex>
      }
      button={
        <Flex flex={1} justify="end">
          <ReadInstructionButton needle="/settings/code-repository-configuration" />
        </Flex>
      }
      footerTemplate={
        <FooterConfigurationTemplate
          disabledSelected={!selected || props.isEmptyConfig}
          aside={
            <Flex flex={1} align="center" gap={8}>
              <ConfigurationBadge type="edited" textColor="secondary" />
              <ConfigurationBadge
                type="vm"
                text={`${t("inventory.vm.drawer.configuration.footer.correlated")} VM's`}
                textColor="secondary"
              />
            </Flex>
          }
          menu={{
            onClick: (info) => {
              confirm((attr) =>
                DeleteConfirmModalConfiguration({
                  entityName: "code-repository-configuration",
                  title: "Reset Code repository configuration",
                  content:
                    "This will restore the default configuration and may change which project issues are assigned to in Triage.",
                  messages: {
                    success: t(
                      "inventory.codeRepositoriesTab.drawer.config.resetSuccess"
                    ),
                    error: t(
                      "inventory.codeRepositoriesTab.drawer.config.errors.resetFailed"
                    )
                  },
                  onOk: async () => {
                    try {
                      if (info.key === "all") return onReset()

                      return handleResetSelected()
                    } catch (err: unknown) {
                      return Promise.reject(err)
                    }
                  },
                  ...attr
                })
              )
            }
          }}
        >
          <Flex gap={8} align="center">
            <Divider orientation="vertical" />
            <Button
              disabled={disabledFooterButtons || loading}
              onClick={onCancel}
            >
              {t("general.cancel")}
            </Button>
            <Button
              disabled={disabledFooterButtons}
              loading={loading}
              onClick={() => {
                const shouldRemoveCorrelationVM = Object.entries(
                  currentConfigs
                ).filter(([key, value]) => {
                  if (!repoPathsVM) return false

                  if (value.imageRepositories.length > 0)
                    return repoPathsVM[key] || repoPathsVM[`/${key}`]

                  return false
                })
                const keys = Object.keys(currentConfigs)
                const stateOfConfig = keys.reduce(
                  (acc, key) => {
                    const { splitPath, imageRepositories, exclude } =
                      currentConfigs[key] || {}

                    if (splitPath === true) acc.splitPath += 1

                    if (imageRepositories.length > 0) {
                      acc.imageRepositories += imageRepositories.length
                      acc.pathImageRepositories += 1
                    }

                    if (exclude === true) acc.excludePath += 1

                    return acc
                  },
                  {
                    splitPath: 0,
                    excludePath: 0,
                    imageRepositories: 0,
                    pathImageRepositories: 0
                  }
                )

                const isSplitPath = stateOfConfig.splitPath > 0
                const isImageRepositories = stateOfConfig.imageRepositories > 0
                const isExcludePath = stateOfConfig.excludePath > 0
                const displayContent =
                  isSplitPath || isImageRepositories || isExcludePath

                if (shouldRemoveCorrelationVM.length > 0)
                  setOpen(shouldRemoveCorrelationVM)
                else
                  confirm((attr) =>
                    CancelConfirmModal({
                      width: 570,
                      title: tConfig("saveModalTitle"),
                      content: (
                        <div>
                          {displayContent && (
                            <span>{tConfig("saveModalContent")}</span>
                          )}
                          <ul className={styles.listContentConfirm}>
                            {isSplitPath && (
                              <li>
                                {tConfig("saveSplit", {
                                  path: tConfig("path", {
                                    count: keys.length
                                  }),
                                  count: stateOfConfig.splitPath
                                })}
                              </li>
                            )}
                            {isImageRepositories && (
                              <li>
                                {tConfig("saveCorrelation", {
                                  path: tConfig("path", {
                                    count: stateOfConfig.pathImageRepositories
                                  }),
                                  image: t("general.image", {
                                    count: stateOfConfig.imageRepositories
                                  })
                                })}
                              </li>
                            )}
                            {isExcludePath && (
                              <li>
                                {tConfig("saveExclude", {
                                  path: tConfig("path", {
                                    count: stateOfConfig.excludePath
                                  })
                                })}
                              </li>
                            )}
                          </ul>
                        </div>
                      ),
                      cancelText: t("general.cancel"),
                      okText: t("general.save"),
                      onOk: () => {
                        handleSave()
                      },
                      ...attr
                    })
                  )
              }}
              type="primary"
              icon={<SaveOutlined />}
            >
              {t("components.configuration.modals.save")}
            </Button>
          </Flex>
        </FooterConfigurationTemplate>
      }
    >
      <Divider className="divider-m-0" />
      <Row className={styles.row}>
        <Col span={10} className={styles.itemWrapper}>
          <div className={styles.scrollable}>
            <ModulesTree
              repo={{ name: repoName, scm }}
              repoPathsVM={repoPathsVM}
              selectedKeys={highlights}
              onSelect={(_, e) => {
                const nodeKey = e.node.key.toString()
                if (nodeKey.length === 0) return
                if (isFile(nodeKey)) {
                  const dir = dirname(nodeKey)
                  if (dir === "") {
                    setSelected(ROOT_KEY)
                    setHighlights([ROOT_KEY])
                    return
                  }
                  setSelected(dir === "" ? ROOT_KEY : dir)
                  setHighlights([
                    dir === "" ? ROOT_KEY : dir,
                    ...allAdjacentModules(nodeKey)
                  ])
                  return
                }
                setSelected(nodeKey)
                setHighlights(
                  nodeKey === ROOT_KEY ? [ROOT_KEY] : getAllKeys(e.node)
                )
              }}
              modules={normalizedModules}
              defaultExpandAll
              edited={edited}
            />
          </div>
        </Col>
        <Col
          span={14}
          className={cn(
            styles.itemWrapper,
            selected && styles.scrollable,
            selected && styles.scrollableRightPanel,
            !selected && styles.emptyItemWrapper
          )}
        >
          {configLoading && <Loader />}
          {!configLoading && (
            <>
              {!!selected && (
                <MonorepoConfigurationsForm
                  projectName={projectName}
                  isRootSelected={selected === ROOT_KEY}
                  onResetSelected={() => onResetSelected()}
                  onChange={(v) => {
                    if (!selected) return
                    setCurrentConfigs((c) => ({
                      ...c,
                      [selected === ROOT_KEY ? "" : selected]: v
                    }))
                  }}
                  values={selectedConfigs}
                />
              )}
              {!selected && <PlaceholderConfiguration />}
            </>
          )}
        </Col>
      </Row>

      <ModalConfigurationTabVirtualMachineDrawer
        open={!!open}
        title={tConfig("vmCorrelatedModal.title")}
        okText={t("components.configuration.modals.detach")}
        onOk={() => {
          handleSave()
          setOpen(false)
        }}
        onCancel={() => setOpen(false)}
      >
        <Flex vertical gap={10}>
          <Text>{tConfig("vmCorrelatedModal.content")}</Text>
          <Flex vertical gap={4}>
            {Array.isArray(open) &&
              open.map((values) => {
                if (!repoPathsVM) return null

                const path = values[0]
                const { vmId } = (repoPathsVM[path] ||
                  repoPathsVM[`/${path}`] ||
                  {}) as { vmId?: string }

                if (!vmId) return null

                return (
                  <Flex
                    align="center"
                    gap={10}
                    key={vmId}
                    className={styles.vmItem}
                  >
                    <VmIcons className={styles.vmIcon} />
                    <Text>{vmId}</Text>
                  </Flex>
                )
              })}
          </Flex>
          <Text>{tConfig("vmCorrelatedModal.warning")}</Text>
        </Flex>
      </ModalConfigurationTabVirtualMachineDrawer>
    </VerticalTabTemplate>
  )
}
